import { useEffect, useCallback, ChangeEvent, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  AppButton,
  AppDivider,
  AppSegment,
  AppFlexContainer,
  AppFlexItem,
  AppProgressBar,
  AppLoader,
} from 'client/ui-building-blocks'

import {
  useAppContext,
  useModalDialogContext,
  usePageStateContext,
  useQuestion,
} from 'client/hooks'

import { ApiUrlPath } from 'cat-shared/utils'
import { useGetRandomQuestion } from 'client/api'
import { Question, QuestionCategorySelect } from 'client/components'
import { QuestionContextMenu } from 'client/components/Question'
import { type TGetRandomQuestionData } from 'client/api/useGetRandomQuestion/use-get-random-question'

import { RandomizerSummary } from './RandomizerSummary/randomizer-summary'

const RandomQuestionPage = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const { isSubmitQuestionFormDirty, randomizerCategory, setRandomizerCategory } =
    usePageStateContext()

  const { authUser } = useAppContext()
  const { toggleSubmitQuestionModal } = useModalDialogContext()

  const form = useForm({
    defaultValues: {
      category: randomizerCategory,
    },
  })

  const {
    data: randomQuestionData,
    refetch: fetchRandomQuestion,
    isLoading: isRandomQuestionLoading,
    isFetching: isRandomQuestionFetching,
  } = useGetRandomQuestion(randomizerCategory)

  useEffect(() => {
    fetchRandomQuestion()
  }, [])

  const randomQuestion = randomQuestionData?.data?.[0] || null

  const { data, isQuestionBeingSubmitted, handleQuestionSubmission } = useQuestion(
    randomQuestion?._id.toString(),
    true
  )

  const onCategoryChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setRandomizerCategory(e.target.value)
    setTimeout(fetchRandomQuestion)
  }, [])

  const isRandomQuestionBeingFetched = isRandomQuestionLoading || isRandomQuestionFetching

  const onNextBtnClick = useCallback(() => {
    if (isRandomQuestionBeingFetched) {
      return
    }
    if (!isSubmitQuestionFormDirty) {
      fetchRandomQuestion()
    } else {
      toggleSubmitQuestionModal(true)
    }
  }, [isRandomQuestionBeingFetched, isSubmitQuestionFormDirty])

  const count = randomQuestionData?.count || null
  const question = data?.data?.[0] || null

  const renderQuestion = useMemo(() => {
    return (
      <Question
        question={question || randomQuestion}
        isBeingSubmitted={isQuestionBeingSubmitted}
        handleQuestionSubmission={formValues => {
          handleQuestionSubmission(formValues).then(() => {
            queryClient.setQueryData(
              [ApiUrlPath.GetRandomQuestion, randomizerCategory],
              (currentData: TGetRandomQuestionData) => {
                if (!currentData) return undefined
                return {
                  ...currentData,
                  count: {
                    ...currentData.count,
                    notAnsweredTotal: currentData.count.notAnsweredTotal - 1,
                  },
                }
              }
            )
          })
        }}
      />
    )
  }, [question, randomQuestion, isQuestionBeingSubmitted, randomizerCategory])

  const answeringDone = count?.total && !count?.notAnsweredTotal

  return (
    <AppSegment
      maxWidth="medium"
      centered
      headerProps={{
        text: t('randomQuestionPage.header'),
        iconProps: { name: 'RANDOM' },
        marginBottom: 'xsmall',
      }}
    >
      <AppProgressBar
        total={count?.total}
        value={count ? count.total - count.notAnsweredTotal : 0}
        label="Answered / Total"
        loading={!randomQuestionData}
      />
      <AppDivider size="xxsmall" />
      <FormProvider {...form}>
        <AppFlexContainer verticalOnMobile>
          <AppFlexItem minWidth="225px">
            <QuestionCategorySelect
              includeAnyCategory
              isDisabled={
                isRandomQuestionBeingFetched ||
                isQuestionBeingSubmitted ||
                !randomQuestionData?.count.total
              }
              onChange={onCategoryChange}
            />
          </AppFlexItem>
        </AppFlexContainer>
      </FormProvider>
      <AppDivider />
      <AppSegment
        headerProps={
          !answeringDone
            ? {
                id: 'randomizer-question-header',
                text: t('question.header'),
                iconProps: { name: 'QUESTION_CIRCLE' },
                marginBottom: 'xsmall',
                rightSlot: (
                  <QuestionContextMenu
                    question={randomQuestion}
                    isDisabled={isRandomQuestionBeingFetched || isQuestionBeingSubmitted}
                    pointing="top right"
                  />
                ),
              }
            : null
        }
      >
        <AppLoader active={isRandomQuestionBeingFetched}>
          {answeringDone ? (
            <>
              <RandomizerSummary
                userId={authUser._id.toString()}
                categoryId={form.getValues().category}
              />
              <AppDivider />
            </>
          ) : null}
          {randomQuestion ? (
            <>
              {renderQuestion}
              {question && !answeringDone ? (
                <AppFlexContainer verticalOnMobile justifyContent="flex-end">
                  <AppFlexItem>
                    <AppDivider size="small" />
                    <AppButton
                      data-testid="question-nav-next"
                      id="question-nav-next"
                      variant="success"
                      text={t('common.buttons.nextQuestion')}
                      iconName="CHEVRON_RIGHT"
                      iconPosition="right"
                      onClick={onNextBtnClick}
                      fullWidth
                    />
                  </AppFlexItem>
                </AppFlexContainer>
              ) : null}
            </>
          ) : null}
        </AppLoader>
      </AppSegment>
    </AppSegment>
  )
}

export default RandomQuestionPage
